import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import CommunityFollowButton from "./CommunityFollowButton";
import { fetchCommunityFollows } from "./communitySocialApi";

/**
 * Horizontal organization card for type-row carousels (Home-collection style).
 */
function OrganizationTypeCard({
  item,
  kindLabel = "Organization",
  showFollow = true,
  following = false,
  onFollowChange = null,
}) {
  const path = item.path || item.public_path || item.href || "#";
  const name = item.name || item.display_name || item.title || kindLabel;
  const cover = item.cover_image_path || item.banner;
  const logo = item.logo_path || item.avatar;
  const meta =
    item.organization_type_label ||
    item.category ||
    kindLabel;
  const summary = item.summary || item.description;
  const orgId = item.id != null ? Number(item.id) : 0;
  const followerCount =
    typeof item.follower_count === "number" ? item.follower_count : null;

  return (
    <article className="org-type-card">
      <Link to={path} className="org-type-card__link" aria-label={`View ${name}`}>
        <div className="org-type-card__cover" aria-hidden={!cover}>
          {cover ? (
            <img
              src={resolveMediaUrl(cover)}
              alt=""
              onError={handleMediaImgError}
            />
          ) : (
            <span className="org-type-card__cover-fallback" />
          )}
          <span className="org-type-card__logo" aria-hidden="true">
            {logo ? (
              <img
                src={resolveMediaUrl(logo)}
                alt=""
                onError={handleMediaImgError}
              />
            ) : (
              <span className="org-type-card__logo-fallback">
                {(name || "?").charAt(0).toUpperCase()}
              </span>
            )}
          </span>
        </div>
        <div className="org-type-card__body">
          {meta ? <p className="org-type-card__meta">{meta}</p> : null}
          <h3 className="org-type-card__name">{name}</h3>
          {summary ? <p className="org-type-card__summary">{summary}</p> : null}
          {item.is_featured || item.is_verified ? (
            <p className="org-type-card__badges">
              {[item.is_featured ? "Featured" : null, item.is_verified ? "Verified" : null]
                .filter(Boolean)
                .join(" · ")}
            </p>
          ) : null}
          {typeof followerCount === "number" ? (
            <p className="org-type-card__followers">
              {followerCount} follower{followerCount === 1 ? "" : "s"}
            </p>
          ) : null}
        </div>
      </Link>
      {showFollow && orgId ? (
        <div className="org-type-card__actions">
          <CommunityFollowButton
            type="organization"
            id={orgId}
            initiallyFollowing={following || !!item.is_following}
            followerCount={followerCount}
            onChange={onFollowChange}
            className="org-type-card__follow"
          />
        </div>
      ) : null}
    </article>
  );
}

/**
 * Organizations directory grouped into horizontal rows by Organization Type.
 * Only renders types with ≥1 org. Order comes from API (admin display_order).
 */
function CommunityOrganizationTypeRows({
  groups = null,
  loading = false,
  error = false,
  emptyTitle = "No organizations yet",
  emptyBody = "Public organization profiles will appear here as communities join.",
  onRetry = null,
  filterType = null,
}) {
  const list = Array.isArray(groups) ? groups : [];
  const filtered = filterType
    ? list.filter(
        (g) =>
          (g.slug || g.type || "").toLowerCase() ===
          String(filterType).toLowerCase()
      )
    : list;
  const visible = filtered.filter(
    (g) => Array.isArray(g.organizations) && g.organizations.length > 0
  );

  const [followingIds, setFollowingIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hasSession =
        localStorage.getItem("userId") && localStorage.getItem("accessToken");
      if (!hasSession) return;
      const result = await fetchCommunityFollows("organization");
      if (cancelled || result.error) return;
      const next = new Set();
      (result.follows || []).forEach((row) => {
        if (row && row.followable_id != null) {
          next.add(Number(row.followable_id));
        }
      });
      setFollowingIds(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFollowChange = (detail) => {
    if (!detail || detail.id == null) return;
    setFollowingIds((prev) => {
      const next = new Set(prev);
      if (detail.following) {
        next.add(Number(detail.id));
      } else {
        next.delete(Number(detail.id));
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="org-type-rows" role="status" aria-live="polite" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={`org-row-skel-${i}`} className="org-type-row org-type-row--skeleton">
            <div className="org-type-row__head">
              <span className="org-type-row__skel-title" />
            </div>
            <div className="org-type-row__track">
              {[0, 1, 2, 3].map((j) => (
                <div key={`skel-card-${i}-${j}`} className="org-type-card org-type-card--skeleton" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-type-rows org-type-rows--error" role="alert">
        <h3 className="community-directory__empty-title">
          Couldn’t load organizations
        </h3>
        <p className="community-directory__empty-body">
          Please try again in a moment.
        </p>
        {typeof onRetry === "function" ? (
          <button type="button" className="btn btn-danger" onClick={onRetry}>
            Retry
          </button>
        ) : (
          <Link to="/home" className="btn btn-outline-secondary">
            Back to Home
          </Link>
        )}
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div className="org-type-rows org-type-rows--empty">
        <h3 className="community-directory__empty-title">{emptyTitle}</h3>
        <p className="community-directory__empty-body">{emptyBody}</p>
        <div className="community-directory__actions">
          <Link to="/home" className="btn btn-danger">
            Explore Home
          </Link>
          <Link to="/community" className="btn btn-outline-secondary">
            Community home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="org-type-rows">
      {visible.map((group) => (
        <OrganizationTypeRow
          key={group.slug || group.type || group.label}
          group={group}
          followingIds={followingIds}
          onFollowChange={handleFollowChange}
        />
      ))}
    </div>
  );
}

function OrganizationTypeRow({ group, followingIds, onFollowChange }) {
  const trackRef = useRef(null);
  const label = group.label || group.name || group.slug || "Organizations";
  const orgs = group.organizations || [];
  const showViewAll = !!group.has_more || orgs.length >= 6;
  const viewAllPath =
    group.view_all_path ||
    `/community/organizations?type=${encodeURIComponent(group.slug || group.type || "")}`;

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.max(240, Math.floor(el.clientWidth * 0.8));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <section className="org-type-row" aria-labelledby={`org-type-${group.slug || "row"}`}>
      <div className="org-type-row__head">
        <div className="org-type-row__titles">
          <h2
            id={`org-type-${group.slug || "row"}`}
            className="org-type-row__title"
          >
            {label}
          </h2>
          {typeof group.count === "number" ? (
            <span className="org-type-row__count">{group.count}</span>
          ) : null}
        </div>
        <div className="org-type-row__controls">
          {showViewAll ? (
            <Link to={viewAllPath} className="org-type-row__view-all">
              View All
            </Link>
          ) : null}
          <button
            type="button"
            className="org-type-row__arrow"
            aria-label={`Scroll ${label} left`}
            onClick={() => scrollBy(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="org-type-row__arrow"
            aria-label={`Scroll ${label} right`}
            onClick={() => scrollBy(1)}
          >
            ›
          </button>
        </div>
      </div>
      <div className="org-type-row__viewport">
        <ul className="org-type-row__track" ref={trackRef}>
          {orgs.map((org, index) => (
            <li
              key={org.id || org.slug || `org-${index}`}
              className="org-type-row__item"
            >
              <OrganizationTypeCard
                item={org}
                following={followingIds.has(Number(org.id))}
                onFollowChange={onFollowChange}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default CommunityOrganizationTypeRows;
export { OrganizationTypeCard };
